/* eslint-disable unicorn/no-null */
import { create } from 'zustand'

import { API_CONFIG, API_ENDPOINTS } from '@/config/api'
import { apiClient } from '@/config/apiClient'
import {
  getAccessToken,
  getCookie,
  removeAccessToken,
  setAccessToken,
} from '@/helpers/cookies'
import { MarketingConsentType } from '@/store/profileStore'

export const STS_TOKEN_COOKIE = 'STS-TOKEN'

interface GravityResponse {
  token: string
  consent: MarketingConsentType | null
  needs: 'Email' | 'Mobile' | null
  email: string
  mobile: string
}

interface SpainAuthResponse {
  token: string
}

interface RomaniaAuthResponse {
  token: string
}

interface BrazilAuthResponse {
  token: string
}

interface AuthState {
  isAuthenticated: boolean
  isGuest: boolean
  accessToken: string | null
  stsToken: string | null
  gravityData: GravityResponse | null
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  initialize: () => Promise<void>
  initializeSpain: (
    credentials: string | undefined,
    token: string
  ) => Promise<void>
  initializeRomania: (userId: string) => Promise<void>
  initializeBrazil: (firebaseToken: string) => Promise<void>
  clearError: () => void
  logout: () => void
}

export const useAuthStore = create<AuthState & AuthActions>(set => ({
  isAuthenticated: false,
  isGuest: false,
  accessToken: null,
  stsToken: null,
  gravityData: null,
  isLoading: false,
  error: null,

  initialize: async () => {
    set({ isLoading: true, error: null })

    try {
      const stsToken = getCookie(STS_TOKEN_COOKIE)

      if (stsToken) {
        const gravityData = (await apiClient.post(
          API_ENDPOINTS.gravityAuth(API_CONFIG.programCode),
          { token: stsToken }
        )) as GravityResponse
        setAccessToken(gravityData.token)
        set({
          isAuthenticated: true,
          isGuest: false,
          accessToken: gravityData.token,
          stsToken,
          gravityData,
          isLoading: false,
        })
        return
      }

      // Fallback: check existing accessToken cookie
      const existingToken = getAccessToken()
      if (existingToken) {
        set({
          isAuthenticated: true,
          isGuest: false,
          accessToken: existingToken,
          isLoading: false,
        })
        return
      }

      // No token — not authenticated
      set({ isAuthenticated: false, isLoading: false })
    } catch (error) {
      removeAccessToken()
      set({ isAuthenticated: false, isLoading: false })
      throw error
    }
  },

  // Spain AGORA flow: exchange credentials + opaque token for MNGS session token.
  // Errors propagate to appStore for routing (401 → error screen, 403 → not-available).
  initializeSpain: async (credentials: string | undefined, token: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = (await apiClient.post(
        API_ENDPOINTS.spainAuth(API_CONFIG.programCode),
        { credentials, token }
      )) as SpainAuthResponse
      setAccessToken(response.token)
      set({
        isAuthenticated: true,
        isGuest: false,
        accessToken: response.token,
        isLoading: false,
      })
    } finally {
      set({ isLoading: false })
    }
  },

  // Romania flow: exchange loyalty card userId (from URL) for MNGS session token.
  // userId is never logged. Errors propagate to appStore for routing.
  initializeRomania: async (userId: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = (await apiClient.post(
        API_ENDPOINTS.romaniaAuth(API_CONFIG.programCode),
        { userId }
      )) as RomaniaAuthResponse
      setAccessToken(response.token)
      set({
        isAuthenticated: true,
        isGuest: false,
        accessToken: response.token,
        isLoading: false,
      })
    } finally {
      set({ isLoading: false })
    }
  },

  // Brazil flow: exchange Firebase JWT from WebView (window['Token-Id']) for MNGS session token.
  // Errors propagate to appStore for routing (401 → error screen).
  initializeBrazil: async (firebaseToken: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = (await apiClient.post(
        API_ENDPOINTS.brazilAuth(API_CONFIG.programCode),
        { token: firebaseToken }
      )) as BrazilAuthResponse
      setAccessToken(response.token)
      set({
        isAuthenticated: true,
        isGuest: false,
        accessToken: response.token,
        isLoading: false,
      })
    } finally {
      set({ isLoading: false })
    }
  },

  clearError: () => set({ error: null }),

  logout: () => {
    removeAccessToken()
    set({
      isAuthenticated: false,
      isGuest: false,
      accessToken: null,
      stsToken: null,
      gravityData: null,
    })
  },
}))
